import { Scheme } from "@/type/schemeType";

export default function getFaqData(pageType: string, fundData: Scheme, fundSector?: string[] | null, fundComepanies?: string[] | null) {
  switch (pageType) {
    case "overview":
      const items = [
        {
          question: `What is the current NAV of ${fundData.scheme_name}?`,
          answer: `Net asset value is the per unit price of the mutual fund. The NAV of funds changes every day. The latest NAV of ${fundData.scheme_name} as on ${fundData.latest_nav_date} is ${fundData.latest_nav}.`,
        },
        {
          question: `What is the expense ratio of ${fundData.scheme_name}?`,
          answer: `The expense ratio of a mutual fund means the charges that you pay to the mutual fund company for managing your investments in the fund. This fund has an expense ratio of 1.37%.`,
        },
        {
          question: `What is the benchmark of ${fundData.scheme_name}?`,
          answer: `${fundData.amc} performance is primarily benchmarked against the NIFTY 500 TRI with NIFTY 50 TRI serving as an addition.`,
        },
        {
          question: `What is the AUM or size of ${fundData.scheme_name}?`,
          answer: `AUM or asset under management is the total value of the assets held by a mutual fund scheme. A fund with a high AUM means a lot of money has been invested in it. The AUM of ${fundData.scheme_name} is Rs ${fundData.asset_size_cr && fundData.asset_size_cr !== "" ? `${parseFloat(fundData.asset_size_cr).toFixed(2)} Crs.` : "- Crs."}`,
        },
        {
          question: `Is ${fundData.scheme_name} a safe fund to invest?`,
          answer: `Every fund has different types of risk which are mentioned in the risk-o-meter. The investors are advised to understand their risk tolerance and decide which type of fund they can invest in.`,
        },
        {
          question: `Should I invest in ${fundData.scheme_name}?`,
          answer: `This fund has a consistency rating of ${fundData.ratings} which indicates that the fund has generated average returns. However, whatever returns that the fund delivers, it does so with exceptional consistency. You can consider this fund if the predictability of returns is what you are looking for.`,
        },
        {
          question: `How to start online SIP in ${fundData.scheme_name}?`,
          answer: `MySIPonline is a dedicated platform to start online SIP or mutual funds in any scheme of mutual funds. Firstly, the investor needs to complete KYC details, choose the best fund, add it to the cart, and make the payment through UPI, net banking, or debit card available on our site.`,
        },
        {
          question: `How can I redeem my investments in ${fundData.scheme_name}?`,
          answer: `Redeeming your investments in ${fundData.scheme_name} is super easy<br><br>
              • If you have invested this fund via MySIPonline, just log into the app, go to the investment section, and place your redemption request<br><br>
              • If you have invested this fund from elsewhere, go to the fund house website and place your redemption request from there.`,
        },
        {
          question: `Is there any brokerage/advisory fees on Mutual Fund SIP transactions?`,
          answer: `At MySIPonline, we do not charge any fees related to brokerage & advisory part. That makes us more accessible and cost-effective for a broader range of investors.`,
        },
        {
          question: `Who manages this fund, and what is their expertise?`,
          answer: `${fundData.fund_manager}, with an extensive experience of 15 years while managing equity & related investments. He is focused on picking quality companies and holds them for a long duration to generate good compounding returns in their funds.`,
        },
      ];

      const insertQuestionsOverview = [];

      if (fundSector) {
        insertQuestionsOverview.push({
          question: `What are the top 5 holdings of ${fundData.scheme_name}?`,
          answer: `The fund's top 5 sectors holdings are ${fundSector}`,
        });
      }

      if (insertQuestionsOverview.length > 0) {
        items.splice(6, 0, ...insertQuestionsOverview);
      }

      return items;

    case "returns":
      return [
        {
          question: "How are returns generated on mutual funds??",
          answer: "When you invest in mutual funds, you can earn in two different ways - through dividends and capital gains. The funds that were invested in stocks provide dividends based on their market earnings.",
        },
        {
          question: "How would I know how much return will I get?",
          answer: "Return on mutual fund depends upon on several factors such as objective, risk-tolerance, period of investment and lastly the type of fund invested in. You can also use the mutual fund calculator tool provided on MYSIPONLINE.",
        },
        {
          question: "Which mutual fund is tax free?",
          answer: "Every mutual fund is taxable except ELSS funds that allows investors to claim the deduction of 80c while filling returns in the income tax. That helps in saving some taxes depending upon the amount of money claimed in the sec 80c.",
        },
        {
          question: "What is the difference between yield and return in mutual funds?",
          answer: "Yield is the amount that dividend money earns during a time period, usually reflected as a percentage. Return is the gain value of how much an investment earns or loses over time, reflected for different holding period of investments.",
        },
        {
          question: `How are the returns of ${fundData.scheme_name}?`,
          answer: `Returns of ${fundData.scheme_name} <br><br> • 6 month - ${fundData.sr_six_month_abs ?? "N/A"}%<br><br> • 1 year - ${fundData.sr_one_year_abs ?? "N/A"}%<br><br> • 3 year - ${fundData.sr_three_year ?? "N/A"}%<br><br> • 5 year - ${fundData.sr_five_year ?? "N/A"}%
                `,
        },
      ];

    case "risk":
      return [
        {
          question: "What is the biggest risk for mutual funds?",
          answer: "There are many risks associated with funds, but the biggest of them is the uncertainty of the market as it is not in anyone's hand to control it.",
        },
        {
          question: "Are mutual funds more risky than stocks?",
          answer: "Since they have direct investments in the equity market, both investors often take on risk. However, mutual funds are a safer investment than stocks since their portfolios are highly diversified and professionally managed, which reduces the risk that comes from individual equities.",
        },
        {
          question: "How to reduce risk in mutual funds?",
          answer: "Using the technique of diversification, which means divide and rule helps us to balance our portfolio. It divides the assets into various different sectors that increase the chances of capital gain.",
        },
        {
          question: "What is the relation between risk and return?",
          answer: "This works on the formula of greater the price, greater will be the returns. More risky the fund is, more chances are there of its potential growth.",
        },
        {
          question: "Which category of fund has lower risks?",
          answer: "Although, each and every fund has its own risk factor. The large-cap funds have lesser risk probability. As they hold a strong presence in the market the risk percentage is lower than other categories.",
        },
      ];

    case "portfolio-details":
      const questions = [
        {
          question: "What is Market cap allocation in the portfolio?",
          answer: 'In mutual funds, "market cap allocation" means how assets of the fund are divided among various market capitalizations, which are based on the total market value of the outstanding shares of stock of a company. Market capitalization falls into three major categories: Large-cap, Mid-cap, Small-cap funds.',
        },
        {
          question: "What is sector allocation in the portfolio?",
          answer: "The distribution of assets among various economic sectors is referred to as sector allocation in a portfolio. These sectors include technology, healthcare, financial, energy, and many others.",
        },
        {
          question: "How do I create a good mutual fund portfolio?",
          answer: "Building a good portfolio requires proper research and knowing about goals that need to be achieved. Set a boundary for your risk capacity, study about different funds and look at its past performance. Balance portfolio according to set goals and stay informed.",
        },
        {
          question: "What is the ideal number of funds to have in a portfolio?",
          answer: "There is no ideal number, but it is always advisable to keep 4-5 funds in the portfolio as it can be easy to manage them and correct decisions can be taken according to it. Diversification is the key to manage and balance your portfolio for smooth growth.",
        },
        {
          question: "How to maintain a good portfolio?",
          answer: "Timely monitoring of the portfolio keeps us aware of the changes in the ongoing market trends. It helps in making plans for any policy change.",
        },
        {
          question: "How does risk tolerance play a role in determining portfolio allocation?",
          answer: "Risk tolerance is an important factor in maintaining portfolio allocation. With the correct mix of assets, the value of investments can improve significantly.",
        },
      ];

      const insertQuestionsPortfoilio = [];

      if (fundSector) {
        insertQuestionsPortfoilio.push({
          question: `Top 5 holdings for ${fundData.scheme_name}?`,
          answer: `The top holdings of this fund are ${fundSector}`,
        });
      }

      if (fundComepanies) {
        insertQuestionsPortfoilio.push({
          question: `Top 5 Sector for ${fundData.scheme_name}?`,
          answer: `The major sectors of this fund are ${fundComepanies}.`,
        });
      }

      if (insertQuestionsPortfoilio.length > 0) {
        questions.splice(2, 0, ...insertQuestionsPortfoilio);
      }

      return questions;

    case "investment-details":
      return [
        {
          question: "What is a mutual fund?",
          answer: "In simple words, a fund is an investor’s safety deposit much like in earlier times when people used to store their savings into a bag of rice, in today’s times that bag has come with a new name that is the Mutual Fund.",
        },
        {
          question: "How many types of mutual funds are there?",
          answer: `There are several types of funds such as:
                • Equity funds, primary investment in stocks and equity shares. Subcategories include large-cap, mid-cap, small-cap funds.
                • Debt funds
                • Money market funds
                • Hybrid funds
                • Specialized funds`,
        },
        {
          question: "What are the benefits of investing in funds?",
          answer: "Investing allows us with an opportunity to earn higher returns on our investment with minimal risks. It grows the capital invested in the long-term and provides a systematic way to secure one’s future.",
        },
        {
          question: "Who gets your money in mutual funds?",
          answer: "The income generated on the funds is the interest gained on the invested amount which is fairly distributed among the investors.",
        },
        {
          question: "Do mutual funds generate income?",
          answer: "Investing in funds is a great way to build capital. Long-term investments have proven to be a successful tool to create generational wealth. Deep analysis of the market and risk tolerance can help investors to earn good returns and generate income as well.",
        },
      ];

    default:
      return [];
  }
}
